import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { ensureOperatorsSuccess } from "../lib/operatorsApi";
import { useAuthStore } from "../stores/authStore";
import { useSearchStore } from "../stores/searchStore";
import type { SavedSearch, SearchFilters } from "../types/database";

interface SaveSearchParams {
  name: string;
  polygon?: GeoJSON.Polygon | null;
  filters?: Partial<SearchFilters>;
  bounds?: SearchFilters["bounds"];
}

/**
 * Call operators-query Edge Function with auth
 */
async function callSavedSearchesAPI<T>(
  token: string | null,
  action: string,
  params: Record<string, unknown> = {}
): Promise<T> {
  if (!token) {
    throw new Error("Not authenticated");
  }

  const { data, error } = await supabase.functions.invoke("operators-query", {
    body: { action, params },
    headers: {
      "X-Portale-Token": token,
    },
  });

  return ensureOperatorsSuccess<T>(data, error);
}

/**
 * Hook for managing saved searches
 * Provides CRUD operations with Supabase Edge Functions
 */
interface UpdateSearchParams {
  searchId: string;
  name?: string;
  polygon?: GeoJSON.Polygon | null;
  filters?: Partial<SearchFilters>;
}

export function useSavedSearches() {
  const queryClient = useQueryClient();
  const { token, isAuthenticated } = useAuthStore();
  const { setSavedSearches, addSavedSearch, removeSavedSearch, updateSavedSearch, setFilters, setDrawnPolygon, setLoadedSearchId, setLoadedSearchSnapshot } =
    useSearchStore();

  // Fetch saved searches
  const {
    data: savedSearches = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["saved-searches"],
    queryFn: async () => {
      const data = await callSavedSearchesAPI<SavedSearch[]>(token, "get_saved_searches");
      // Sync with store
      setSavedSearches(data || []);
      return data as SavedSearch[];
    },
    staleTime: 60000, // 1 minute
    enabled: isAuthenticated && !!token,
  });

  // Save search mutation
  const saveMutation = useMutation({
    mutationFn: async (params: SaveSearchParams) => {
      return callSavedSearchesAPI<SavedSearch>(token, "save_search", {
        name: params.name,
        polygon: params.polygon || null,
        filters: params.filters || null,
        bounds: params.bounds || null,
      });
    },
    onSuccess: (newSearch: SavedSearch) => {
      // Add to store
      addSavedSearch(newSearch);
      // Invalidate query to refetch
      queryClient.invalidateQueries({ queryKey: ["saved-searches"] });
    },
  });

  // Delete search mutation
  const deleteMutation = useMutation({
    mutationFn: async (searchId: string) => {
      return callSavedSearchesAPI<{ deleted: boolean }>(token, "delete_search", { searchId });
    },
    onSuccess: (_, searchId) => {
      // Remove from store
      removeSavedSearch(searchId);
      // Invalidate query
      queryClient.invalidateQueries({ queryKey: ["saved-searches"] });
    },
  });

  // Update search mutation
  const updateMutation = useMutation({
    mutationFn: async (params: UpdateSearchParams) => {
      return callSavedSearchesAPI<SavedSearch>(token, "update_search", {
        searchId: params.searchId,
        name: params.name,
        polygon: params.polygon,
        filters: params.filters,
      });
    },
    onSuccess: (updatedSearch: SavedSearch) => {
      // Update in store
      updateSavedSearch(updatedSearch.id, updatedSearch);
      // Invalidate query to refetch
      queryClient.invalidateQueries({ queryKey: ["saved-searches"] });
    },
  });

  // Load a saved search into current filters
  const loadSearch = (search: SavedSearch) => {
    // Set the loaded search ID to track which search is active
    setLoadedSearchId(search.id);

    // Parse filters
    const savedFilters = search.filters as Record<string, unknown> | null;
    // Parse polygon (supports legacy saved searches where polygon was inside filters)
    const parsePolygon = (value: unknown): GeoJSON.Polygon | null => {
      if (!value) return null;
      try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        return parsed && (parsed as GeoJSON.Polygon).type === "Polygon"
          ? (parsed as GeoJSON.Polygon)
          : null;
      } catch (error) {
        console.warn("[useSavedSearches] Impossibile leggere il poligono salvato:", error);
        return null;
      }
    };

    const polygon =
      parsePolygon(search.polygon) ??
      parsePolygon(savedFilters?.polygon);

    const dateRange = savedFilters?.dateRange as { start?: string; end?: string } | undefined;
    const categories =
      (savedFilters?.categories as string[] | undefined) ||
      (savedFilters?.category ? [savedFilters.category as string] : undefined);
    const types =
      (savedFilters?.types as string[] | undefined) ||
      (savedFilters?.type ? [savedFilters.type as string] : undefined);

    const filtersToApply = savedFilters
      ? {
        categories,
        types,
        region: (savedFilters.region as string) || undefined,
        searchText: (savedFilters.searchText as string) || undefined,
        dateRange: dateRange
          ? {
            start: dateRange.start ? new Date(dateRange.start) : null,
            end: dateRange.end ? new Date(dateRange.end) : null,
          }
          : undefined,
      }
      : {};

    // Save snapshot of original values for comparison
    setLoadedSearchSnapshot({
      polygon: polygon as GeoJSON.Polygon | null,
      filters: filtersToApply,
    });

    // Apply polygon
    setDrawnPolygon(polygon as GeoJSON.Polygon | null);

    // Apply filters
    if (savedFilters) {
      setFilters(filtersToApply);
    }
  };

  // Revert to original loaded search values
  const revertToSnapshot = () => {
    const snapshot = useSearchStore.getState().loadedSearchSnapshot;
    if (!snapshot) return;

    setDrawnPolygon(snapshot.polygon);
    setFilters(snapshot.filters);
  };

  // Clear the loaded search (when user modifies filters manually)
  const clearLoadedSearch = () => {
    setLoadedSearchId(null);
    setLoadedSearchSnapshot(null);
  };

  return {
    // Data
    savedSearches,
    isLoading,
    error,

    // Actions
    saveSearch: saveMutation.mutateAsync,
    updateSearch: updateMutation.mutateAsync,
    deleteSearch: deleteMutation.mutateAsync,
    loadSearch,
    clearLoadedSearch,
    revertToSnapshot,
    refetch,

    // Mutation states
    isSaving: saveMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    saveError: saveMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
  };
}
